import { useTranslation } from '@govard/i18n-core/react';
import { useI18nSubscription } from '../../../i18n/useI18nSubscription.js';
import { i18n } from '../../../../i18n/index.js';
import type { ReactElement } from 'react';
import { Badge, Empty } from 'antd';
import { AlertTriangle, CheckCircle2, Info } from 'lucide-react';
import { Link } from 'react-router-dom';

import type { StructuredMessagePayload } from '@govard/ai-chat-kit';

export type SalesHealthOverview = Extract<
  StructuredMessagePayload,
  { type: 'sales_health' }
>['overview'];

const STAGE_LABEL_KEYS: Record<string, string> = {
  New: 'crm.chat.sales_health.stage_New',
  Contacted: 'crm.chat.sales_health.stage_Contacted',
  Negotiation: 'crm.chat.sales_health.stage_Negotiation',
};

type SalesHealthLevel = NonNullable<SalesHealthOverview['topDeals'][number]['healthLevel']>;

function levelBadge(level: SalesHealthLevel): { className: string; icon: ReactElement } {
  switch (level) {
    case 'attention':
      return {
        className:
          'border-[var(--ant-color-warning-border)] bg-[var(--ant-color-warning-bg)] text-[var(--ant-color-warning-text)]',
        icon: <Info size={11} aria-hidden="true" />,
      };
    case 'critical':
      return {
        className:
          'border-[var(--ant-color-error-border)] bg-[var(--ant-color-error-bg)] text-[var(--ant-color-error-text)]',
        icon: <AlertTriangle size={11} aria-hidden="true" />,
      };
    default:
      return {
        className:
          'border-[var(--ant-color-success-border)] bg-[var(--ant-color-success-bg)] text-[var(--ant-color-success-text)]',
        icon: <CheckCircle2 size={11} aria-hidden="true" />,
      };
  }
}

function localizedReason(reason: string): string {
  return i18n.t(`crm.chat.sales_health.reason_${reason}`) as string;
}

function localizedRecommendation(code: string): string {
  return i18n.t(`crm.chat.sales_health.recommendation_${code}`) as string;
}

/** Actionable codes come from the shared risk engine; context codes are secondary. */
const SIGNAL_REASON_CODES = new Set([
  'overdue_next_activity',
  'missing_next_activity',
  'stale_activity',
  'overdue_task',
  'stage_age_over_norm',
]);

function mainReasonFor(reasons: readonly string[]): string | undefined {
  return reasons.find((reason) => SIGNAL_REASON_CODES.has(reason)) ?? reasons[0];
}

function formatMoney(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(i18n.locale === 'ru' ? 'ru-RU' : 'en-US', {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${amount} ${currency}`;
  }
}

function StatTile({ label, value }: { label: string; value: number }): ReactElement {
  return (
    <div className="rounded-md border border-[var(--ant-color-border)] bg-white px-3 py-2">
      <p className="text-[11px] uppercase tracking-wider text-[var(--ant-color-text-secondary)]">
        {label}
      </p>
      <p className="text-lg font-semibold leading-tight text-[var(--ant-color-text-heading)]">
        {value}
      </p>
    </div>
  );
}

export function SalesHealthSnippet({ overview }: { overview: SalesHealthOverview }): ReactElement {
  useI18nSubscription();
  const { t } = useTranslation();

  const hasDeals = overview.activeDealCount > 0;

  return (
    <div
      data-testid="structured-snippet-sales-health"
      className="my-2 overflow-hidden rounded-xl border border-[var(--ant-color-border)] bg-white"
    >
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[var(--ant-color-border)] px-3.5 py-2.5">
        <span className="text-xs font-semibold text-[var(--ant-color-text-heading)]">
          {t('crm.chat.sales_health.title')}
        </span>
        <span className="flex items-center gap-1.5">
          <Badge
            status="default"
            text={
              <span className="text-[10px] font-medium uppercase tracking-wide text-[var(--ant-color-text-secondary)]">
                {t('crm.chat.sales_health.badge_readonly')}
              </span>
            }
          />
          <Badge
            status="warning"
            text={
              <span className="text-[10px] font-medium uppercase tracking-wide text-[var(--ant-color-warning-text-active)]">
                {t('crm.chat.sales_health.badge_heuristic')}
              </span>
            }
          />
        </span>
      </div>

      {!hasDeals ? (
        <div className="p-3.5">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span data-testid="sales-health-empty">
                {t('crm.chat.sales_health.empty_no_deals')}
              </span>
            }
          />
        </div>
      ) : (
        <div className="space-y-3.5 p-3.5">
          <section
            data-testid="sales-health-summary"
            className="grid grid-cols-2 gap-2 sm:grid-cols-4"
          >
            <StatTile
              label={t('crm.chat.sales_health.stats_active')}
              value={overview.activeDealCount}
            />
            <StatTile
              label={t('crm.chat.sales_health.stats_healthy')}
              value={overview.healthyCount}
            />
            <StatTile
              label={t('crm.chat.sales_health.stats_attention')}
              value={overview.attentionCount}
            />
            <StatTile
              label={t('crm.chat.sales_health.stats_critical')}
              value={overview.criticalCount}
            />
          </section>
          <section data-testid="sales-health-money" className="space-y-1.5">
            {overview.totalsByCurrency.map((totals) => (
              <div
                key={totals.currency}
                className="flex flex-wrap items-baseline justify-between gap-x-4 rounded-md bg-[var(--color-surface)] px-3 py-2"
              >
                <span className="font-mono text-[11px] font-semibold text-[var(--ant-color-text-secondary)]">
                  {totals.currency}
                </span>
                <span className="text-xs text-[var(--ant-color-text)]">
                  {t('crm.chat.sales_health.money_pipeline')}:{' '}
                  <b className="font-mono">{formatMoney(totals.pipelineAmount, totals.currency)}</b>
                </span>
                <span className="text-xs text-[var(--ant-color-text)]">
                  {t('crm.chat.sales_health.money_expected')}:{' '}
                  <b className="font-mono text-[var(--ant-color-primary)]">
                    {formatMoney(totals.expectedRevenue, totals.currency)}
                  </b>
                </span>
              </div>
            ))}
          </section>

          <section data-testid="sales-health-stages">
            <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wider text-[var(--ant-color-text-secondary)]">
              {t('crm.chat.sales_health.stages_title')}
            </p>
            <ul className="space-y-1">
              {overview.stages.map((stage) => (
                <li
                  key={stage.stage}
                  className="rounded-md border border-[var(--ant-color-border)] px-3 py-1.5"
                >
                  <span className="text-xs font-medium text-[var(--ant-color-text)]">
                    {t(STAGE_LABEL_KEYS[stage.stage] ?? stage.stage)} · {stage.dealCount}
                  </span>
                  {/* Every currency of the stage is shown compactly; none merged. */}
                  <ul
                    data-testid={`sales-health-stage-money-${stage.stage}`}
                    className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5"
                  >
                    {stage.totalsByCurrency.map((totals) => (
                      <li
                        key={totals.currency}
                        className="font-mono text-[11px] text-[var(--ant-color-text-secondary)]"
                      >
                        {totals.currency} {formatMoney(totals.pipelineAmount, totals.currency)}
                        {' → '}
                        <span className="text-[var(--ant-color-primary)]">
                          {formatMoney(totals.expectedRevenue, totals.currency)}
                        </span>
                      </li>
                    ))}
                    {stage.totalsByCurrency.length === 0 ? <li>—</li> : null}
                  </ul>
                </li>
              ))}
            </ul>
          </section>

          <section data-testid="sales-health-attention">
            <p className="mb-1.5 text-[13px] font-semibold text-[var(--ant-color-text)]">
              {t('crm.chat.sales_health.attention_title')}
            </p>
            {overview.topDeals.length === 0 ? (
              <p className="rounded-md border border-[var(--ant-color-success-border)] bg-[var(--ant-color-success-bg)] px-3 py-2 text-xs text-[var(--ant-color-success-text)]">
                {t(
                  overview.healthyCount === overview.activeDealCount
                    ? 'crm.chat.sales_health.empty_all_healthy'
                    : 'crm.chat.sales_health.empty_no_risky',
                )}
              </p>
            ) : (
              <ul className="space-y-2">
                {overview.topDeals.map((deal) => {
                  const badge = levelBadge(deal.healthLevel);
                  const mainReason = mainReasonFor(deal.reasons);
                  return (
                    <li
                      key={deal.id}
                      className="rounded-md border border-[var(--ant-color-border)] p-2.5 transition-colors hover:bg-[var(--ant-color-primary-bg)]"
                    >
                      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                        <Link
                          to={`/deals/${deal.id}`}
                          data-testid={`sales-health-deal-link-${deal.id}`}
                          className="text-sm font-semibold text-[var(--ant-color-primary)] hover:underline"
                        >
                          {deal.title}
                        </Link>
                        <span
                          data-testid={`sales-health-level-${deal.id}`}
                          className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${badge.className}`}
                        >
                          {badge.icon}
                          {t(`crm.chat.sales_health.level_${deal.healthLevel}`)}
                        </span>
                      </div>
                      <p className="mt-1 text-[11px] text-[var(--ant-color-text-secondary)]">
                        {t(STAGE_LABEL_KEYS[deal.stage] ?? deal.stage)} ·{' '}
                        {formatMoney(deal.amount, deal.currency)} ·{' '}
                        {t('crm.chat.sales_health.col_probability')}: {deal.closeProbability}% ·{' '}
                        {t('crm.chat.sales_health.activity_' + deal.nextActivityState)}
                      </p>
                      <p className="mt-1 text-xs text-[var(--ant-color-text)]">
                        {mainReason === undefined ? null : (
                          <span
                            data-testid={`sales-health-action-${deal.id}`}
                            className="inline-flex flex-wrap items-center gap-1"
                          >
                            <AlertTriangle
                              size={11}
                              className="text-[var(--ant-color-warning-text)]"
                              aria-hidden="true"
                            />
                            {localizedReason(mainReason)}
                            {' → '}
                          </span>
                        )}
                        <b>{localizedRecommendation(deal.recommendationCode)}</b>
                      </p>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </div>
      )}

      <p className="border-t border-[var(--ant-color-border)] bg-[var(--color-surface)] px-3.5 py-2 text-[11px] leading-relaxed text-[var(--ant-color-text-secondary)]">
        {t('crm.chat.sales_health.disclaimer_heuristic')}{' '}
        {t('crm.chat.sales_health.disclaimer_readonly')}
      </p>
    </div>
  );
}
